/**
 * @file src/utils/client/sentryEventFilters.ts
 * @description Sentry beforeSend predicates that drop third-party noise events at the client boundary.
 */

// Featurebase changelog widget (third-party React SDK loaded from do.featurebase.app) floods Sentry
// via captureConsoleIntegration — SONDHEIM-DKT ("Error loading image: [object Event]") and SONDHEIM-CVV
// ("[Featurebase SDK] CSS fetch failed, changelog card will not render"). These are unactionable: the
// payload is a React SyntheticEvent and neither message exists in our first-party code.
// Scoped to the Featurebase SDK so first-party image errors (e.g. ActivityImageV2, SONDHEIM-DV0) keep
// reporting. See https://linear.app/padlet/issue/TA-5222 — do NOT widen the message list.
interface SentryEventLike {
  message?: string
  logger?: string
  exception?: {
    values?: Array<{
      value?: string
      stacktrace?: { frames?: Array<{ filename?: string; in_app?: boolean }> }
    }>
  }
}

const FEATUREBASE_NOISE_PATTERNS = ['Error loading image', '[Featurebase SDK] CSS fetch failed']

export function isFeaturebaseSdkNoiseEvent(event: SentryEventLike): boolean {
  const message = String(event.message ?? event.exception?.values?.[0]?.value ?? '')

  if (!FEATUREBASE_NOISE_PATTERNS.some((pattern) => message.includes(pattern))) {
    return false
  }

  const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? []
  const fromFeaturebaseSdk = frames.some((f) => f.filename?.includes('do.featurebase.app'))
  const hasFirstPartyFrame = frames.some((f) => f.in_app === true)

  return fromFeaturebaseSdk || (event.logger === 'console' && !hasFirstPartyFrame)
}
